import { Component, Input, OnChanges, OnDestroy, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AdminPermitDetail, PermitFile, permitFileName, isPermitFileImage, isPermitFilePdf, isPermitFileWord, isPermitFileExcel, safePermitFileUrl } from '../../models/permit.model';
import { PermitV2Service } from '../../services/permits.abstract.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { BcitiConfirmDialogComponent } from '../../../global/components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';

@Component({
    selector: 'app-permit-internal-files',
    templateUrl: './permit-internal-files.component.html',
    styleUrls: ['./permit-internal-files.component.scss'],
})
export class PermitInternalFilesComponent implements OnChanges, OnDestroy {
    @Input() permit: AdminPermitDetail;
    @Output() permitUpdated = new EventEmitter<void>();

    internalFiles: PermitFile[] = [];
    uploading = false;

    private destroy$ = new Subject<void>();

    constructor(
        private permitService: PermitV2Service,
        private dialog: MatDialog,
        private translate: TranslateService,
        private messageService: MessageService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.permit) {
            this.internalFiles = this.permit?.internalFiles ? [...this.permit.internalFiles] : [];
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    isImage(file: PermitFile): boolean {
        return isPermitFileImage(file);
    }

    isDocumentPdf(file: PermitFile): boolean {
        return isPermitFilePdf(file);
    }

    isDocumentWord(file: PermitFile): boolean {
        return isPermitFileWord(file);
    }

    isDocumentExcel(file: PermitFile): boolean {
        return isPermitFileExcel(file);
    }

    getFileName(file: PermitFile): string {
        return permitFileName(file);
    }

    openFile(file: PermitFile): void {
        const url = safePermitFileUrl(file);
        if (!url) return;
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input?.files?.length) return;
        const file = input.files[0];
        this.uploadAndAttach(file);
        input.value = '';
    }

    uploadAndAttach(file: File): void {
        this.uploading = true;
        this.permitService
            .uploadTempFile(file)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (urls: string[]) => {
                    const url = Array.isArray(urls) ? urls[0] : (urls as any);
                    this.permitService
                        .attachInternalFiles(this.permit._id, { files: [{ url, originalName: file.name }] })
                        .pipe(take(1), takeUntil(this.destroy$))
                        .subscribe({
                            next: () => {
                                this.uploading = false;
                                this.permitUpdated.emit();
                            },
                            error: () => {
                                this.uploading = false;
                                this.messageService.error(this.translate.instant('permit.manage.common.error'));
                            },
                        });
                },
                error: () => {
                    this.uploading = false;
                    this.messageService.error(this.translate.instant('permit.manage.common.error'));
                },
            });
    }

    confirmDelete(index: number): void {
        const confirmText = this.translate.instant('permit.manage.internal_files.delete_confirm');
        const cancelLabel = this.translate.instant('permit.manage.common.cancel');
        const confirmLabel = this.translate.instant('permit.manage.common.delete');

        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            width: '400px',
            data: {
                dialog: {
                    title: confirmText,
                    confirmLabel,
                    cancelLabel,
                    confirmButtonClass: 'danger-btn',
                },
                component: { data: {}, entry: null },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    this.executeDelete(index);
                }
            });
    }

    private executeDelete(index: number): void {
        this.permitService
            .deleteInternalFile(this.permit._id, index)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.permitUpdated.emit();
                },
                error: () => {
                    this.messageService.error(this.translate.instant('permit.manage.common.error'));
                },
            });
    }
}
